import MatchTable, { MatchTableProps } from '@amf/shared/components/match/MatchTable';
import Button from '@amf/shared/components/button/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MatchListItemFragment,
  PlayerListItemFragment,
  RefereeListItemFragment,
  SeasonFragment,
  TeamListItemFragment,
} from '@amf/shared/types/graphql';
import { useSWRInfinite } from 'swr';
import { useTranslate } from '@tolgee/react';
import { getCacheKey, LIMIT } from '@amf/shared/data/Match';
import Loader from '@amf/shared/components/utils/Loader';
import Empty from '@amf/shared/components/utils/Empty';
import { findTeamDefaults } from '@amf/shared/data/Team';
import SeasonSelect, { SeasonSelectData } from '@amf/shared/components/menu/SeasonSelect';
import SelectField from '@amf/shared/components/form/SelectField';

interface TeamMatchesProps extends MatchTableProps {
  team?: TeamListItemFragment;
  player?: PlayerListItemFragment;
  referee?: RefereeListItemFragment;
  seasons: SeasonFragment[];
  dateFrom?: string;
  dateTo?: string;
  orderDesc?: boolean;
}

type SortBy = 'round' | 'time';

function TeamMatches({
  title,
  seasons,
  team,
  player,
  referee,
  matches: initialData,
  dateFrom,
  dateTo,
  orderDesc,
  ...props
}: TeamMatchesProps) {
  const { t } = useTranslate();
  const [sortBy, setSortBy] = useState<SortBy>(referee ? 'time' : 'round');

  const defaultValues: SeasonSelectData = useMemo(() => {
    const { league, season } = findTeamDefaults(team, seasons);

    if (referee) {
      return {
        league: '-',
        season: season?.id ?? seasons[0]?.id,
      };
    }

    return {
      league: league?.id ?? seasons[0]?.leagues[0]?.id,
      season: season?.id ?? seasons[0]?.id,
    };
  }, [referee, seasons, team]);

  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<SeasonSelectData>(defaultValues);
  const { data, size, setSize } = useSWRInfinite<MatchListItemFragment[]>(
    getCacheKey({
      ...filter,
      team: team?.id,
      player: player?.id,
      referee: referee?.id,
      sortBy,
      orderDesc,
      dateFrom,
      dateTo,
    }),
  );

  const showMatchesWithoutSections = useMemo<boolean>(() => {
    return sortBy === 'time' && filter.league === '-';
  }, [filter, sortBy]);

  useEffect(() => {
    setFilter(defaultValues);
  }, [defaultValues, seasons, team, referee]);

  const update = useCallback(
    async (data: SeasonSelectData) => {
      setLoading(true);
      setFilter(data);
      await setSize(0);
      await setSize(1);
      setLoading(false);
    },
    [setSize],
  );

  const matches: MatchListItemFragment[] = data
    ? ([] as MatchListItemFragment[]).concat(...data)
    : [];

  const renderLoadMore = () => {
    if (!matches || data === undefined) {
      return null;
    }

    if (size > data.length) {
      return (
        <Button type='button' variant='dark-outline' disabled>
          {t('team.matches.loading')}
        </Button>
      );
    }

    if (LIMIT * size > matches.length) {
      return null;
    }

    return (
      <Button type='button' variant='dark-outline' onClick={() => setSize(size + 1)}>
        {t('team.matches.more')}
      </Button>
    );
  };

  return (
    <div className='TeamMatches'>
      <div className='TeamMatches__header'>
        {title && <h2 className='text-2xl font-medium'>{title}</h2>}
        <div className='TeamMatches__toolbar'>
          <SeasonSelect
            team={team}
            values={defaultValues}
            seasons={seasons}
            onUpdate={update}
            allSeasonsAllowed
            allLeaguesAllowed
          />
          <SelectField
            label={t('team.matches.sortBy')}
            value={sortBy}
            onChange={e => setSortBy(e.target.value as SortBy)}
          >
            <option value='round'>{t('team.matches.sortBy.round')}</option>
            <option value='time'>{t('team.matches.sortBy.time')}</option>
          </SelectField>
        </div>
      </div>
      {loading && <Loader>{t('team.matches.loading')}</Loader>}
      {!loading && matches.length === 0 && <Empty>{t('team.matches.empty')}</Empty>}
      {!loading && matches.length > 0 && (
        <MatchTable phasesInRow={showMatchesWithoutSections} phases matches={matches} {...props} />
      )}
      <div className='TeamMatches__pagination'>{renderLoadMore()}</div>
    </div>
  );
}

export default TeamMatches;
